import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getStoredAuth, login as apiLogin, logout as apiLogout, me } from "@/lib/apiClient";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => getStoredAuth());
  const [user, setUser] = useState(auth?.user || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (auth && !user) {
      me()
        .then((u) => setUser(u))
        .catch(() => {
          setAuth(null);
          setUser(null);
        });
    }
  }, [auth, user]);

  const doLogin = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const data = await apiLogin(email, password);
      setAuth(data);
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const doLogout = useCallback(async () => {
    await apiLogout();
    setAuth(null);
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, isAuthenticated: !!user, loading, login: doLogin, logout: doLogout }),
    [user, loading, doLogin, doLogout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

// V2.0 — Consolidated operational role bundle. All five roles share
// identical permissions. Include this set anywhere an "operational" action
// is being gated.
const OPERATIONAL_ROLES = [
  "OPERATIONS_ENGINEER",
  "QC_ENGINEER",
  "PREPARATION_ENGINEER",
  "BATTERY_ENGINEER",
  "WAREHOUSE",
];

const CAN_ACCESS_ADMIN = ["SUPER_ADMIN", "ADMIN", "RECEPTION", "SALES", "ACCOUNTS", "AI_WARRANTY_AGENT", ...OPERATIONAL_ROLES];
const CAN_MANAGE_MASTERS = ["SUPER_ADMIN", "ADMIN"];
const CAN_PROCURE = ["SUPER_ADMIN", "ADMIN", "RECEPTION"];
const CAN_MANAGE_TELEPHONY = ["SUPER_ADMIN"];
// V2.0 — All operational roles perform QC, Preparation, Battery Testing,
// Warehouse Ops, Customer Cases, Warranty Verification and Repairs identically.
const CAN_QC = ["SUPER_ADMIN", "ADMIN", ...OPERATIONAL_ROLES];
const CAN_FINISH_QC = ["SUPER_ADMIN", "ADMIN", ...OPERATIONAL_ROLES];
const CAN_INV_READ = ["SUPER_ADMIN", "ADMIN", "RECEPTION", ...OPERATIONAL_ROLES];
const CAN_INV_LOCATION = ["SUPER_ADMIN", "ADMIN", ...OPERATIONAL_ROLES];
const CAN_INV_RESERVE = ["SUPER_ADMIN", "ADMIN", "RECEPTION"];
const CAN_INV_PACK_SHIP = ["SUPER_ADMIN", "ADMIN", ...OPERATIONAL_ROLES];
const CAN_INV_SCRAP_LOST = ["SUPER_ADMIN"];
const CAN_SALES_READ = ["SUPER_ADMIN", "ADMIN", "SALES", "RECEPTION", "ACCOUNTS", ...OPERATIONAL_ROLES];
const CAN_SALES_WRITE = ["SUPER_ADMIN", "ADMIN", "SALES", "RECEPTION"];
const CAN_ACCOUNTS = ["SUPER_ADMIN", "ADMIN", "ACCOUNTS", "SALES", "RECEPTION"];
const CAN_BACKUP_READ = ["SUPER_ADMIN", "ADMIN"];
const CAN_BACKUP_WRITE = ["SUPER_ADMIN"];
const CAN_CASES_READ = ["SUPER_ADMIN", "ADMIN", "RECEPTION", "SALES", "ACCOUNTS", "AI_WARRANTY_AGENT", ...OPERATIONAL_ROLES];
const CAN_CASES_AUTHOR = ["SUPER_ADMIN", "ADMIN", "RECEPTION", "SALES", "ACCOUNTS"];
const CAN_CASES_HANDLE = ["SUPER_ADMIN", "ADMIN", ...OPERATIONAL_ROLES];
const CAN_CASES_APPROVE = ["SUPER_ADMIN", "ADMIN"];
const CAN_WARRANTY_READ = ["SUPER_ADMIN", "ADMIN", "RECEPTION", "SALES", "ACCOUNTS", "AI_WARRANTY_AGENT", ...OPERATIONAL_ROLES];
const CAN_WARRANTY_WRITE = ["SUPER_ADMIN", "ADMIN", "SALES", "RECEPTION", "ACCOUNTS"];
const CAN_WARRANTY_VOID = ["SUPER_ADMIN", "ADMIN"];
const CAN_CAMPAIGNS_READ = ["SUPER_ADMIN", "ADMIN", "AI_WARRANTY_AGENT", "SALES", "ACCOUNTS", "RECEPTION"];
const CAN_CAMPAIGNS_WRITE = ["SUPER_ADMIN", "ADMIN"];
// Vendor Returns — mirror backend roles in app/routers/vendor_returns.py.
const CAN_VR_WRITE = ["SUPER_ADMIN", "ADMIN", "WAREHOUSE", "QC_ENGINEER", "OPERATIONS_ENGINEER", "PREPARATION_ENGINEER"];
const CAN_VR_READ = [...CAN_VR_WRITE, "SALES", "RECEPTION", "ACCOUNTS"];
// V1.1 Workflow Phase 3 — SUPER_ADMIN + RECEPTION may delete a Purchase.
const CAN_DELETE_RECORDS = ["SUPER_ADMIN", "RECEPTION"];
// Customer Case deletion — SUPER_ADMIN + RECEPTION only.
const CAN_CASES_DELETE = ["SUPER_ADMIN", "RECEPTION"];
// V2.0 — Receive Stock, QC-Complete and Battery are operational actions.
const CAN_RECEIVE = ["SUPER_ADMIN", "ADMIN", ...OPERATIONAL_ROLES];
const CAN_QC_COMPLETE = ["SUPER_ADMIN", "ADMIN", ...OPERATIONAL_ROLES];
const CAN_BATTERY = ["SUPER_ADMIN", "ADMIN", ...OPERATIONAL_ROLES];
const CAN_MANAGE_COMPANY_SETTINGS = ["SUPER_ADMIN", "ADMIN"];
const CAN_SYSTEM_MAINTENANCE = ["SUPER_ADMIN"];
// Rental / Asset Allocation — open to ALL internal staff roles (not external
// rental-party portal accounts, which authenticate separately).
const CAN_RENTAL = ["SUPER_ADMIN", "ADMIN", "RECEPTION", "SALES", "ACCOUNTS", "AI_WARRANTY_AGENT", ...OPERATIONAL_ROLES];
// Editing a Rental Asset ID is limited to SUPER_ADMIN / ADMIN / RECEPTION.
const CAN_RENTAL_EDIT_ASSET_ID = ["SUPER_ADMIN", "ADMIN", "RECEPTION"];

export const canAccessAdmin = (user) => user && CAN_ACCESS_ADMIN.includes(user.role);
export const canManageMasters = (user) => user && CAN_MANAGE_MASTERS.includes(user.role);
export const canProcure = (user) => user && CAN_PROCURE.includes(user.role);
export const canManageTelephony = (user) => user && CAN_MANAGE_TELEPHONY.includes(user.role);
export const canQC = (user) => user && CAN_QC.includes(user.role);
export const canFinishQC = (user) => user && CAN_FINISH_QC.includes(user.role);
export const canInventoryRead = (user) => user && CAN_INV_READ.includes(user.role);
export const canInventoryLocation = (user) => user && CAN_INV_LOCATION.includes(user.role);
export const canInventoryReserve = (user) => user && CAN_INV_RESERVE.includes(user.role);
export const canInventoryPackShip = (user) => user && CAN_INV_PACK_SHIP.includes(user.role);
export const canInventoryScrapLost = (user) => user && CAN_INV_SCRAP_LOST.includes(user.role);
export const canSalesRead = (user) => user && CAN_SALES_READ.includes(user.role);
export const canSalesWrite = (user) => user && CAN_SALES_WRITE.includes(user.role);
export const canAccounts = (user) => user && CAN_ACCOUNTS.includes(user.role);
export const canBackupRead = (user) => user && CAN_BACKUP_READ.includes(user.role);
export const canBackupWrite = (user) => user && CAN_BACKUP_WRITE.includes(user.role);
export const canCasesRead = (user) => user && CAN_CASES_READ.includes(user.role);
export const canCasesAuthor = (user) => user && CAN_CASES_AUTHOR.includes(user.role);
export const canCasesHandle = (user) => user && CAN_CASES_HANDLE.includes(user.role);
export const canCasesApprove = (user) => user && CAN_CASES_APPROVE.includes(user.role);
export const canCasesDelete = (user) => user && CAN_CASES_DELETE.includes(user.role);
export const canWarrantyRead = (user) => user && CAN_WARRANTY_READ.includes(user.role);
export const canWarrantyWrite = (user) => user && CAN_WARRANTY_WRITE.includes(user.role);
export const canWarrantyVoid = (user) => user && CAN_WARRANTY_VOID.includes(user.role);
export const canCampaignsRead = (user) => user && CAN_CAMPAIGNS_READ.includes(user.role);
export const canCampaignsWrite = (user) => user && CAN_CAMPAIGNS_WRITE.includes(user.role);
export const canVendorReturnsRead = (user) => user && CAN_VR_READ.includes(user.role);
export const canVendorReturnsWrite = (user) => user && CAN_VR_WRITE.includes(user.role);
// V1.1 Workflow Phase 3
export const canDeleteRecords = (user) => user && CAN_DELETE_RECORDS.includes(user.role);
export const canReceive = (user) => user && CAN_RECEIVE.includes(user.role);
export const canQCComplete = (user) => user && CAN_QC_COMPLETE.includes(user.role);
export const canBattery = (user) => user && CAN_BATTERY.includes(user.role);
export const canManageCompanySettings = (user) => user && CAN_MANAGE_COMPANY_SETTINGS.includes(user.role);
export const canSystemMaintenance = (user) => user && CAN_SYSTEM_MAINTENANCE.includes(user.role);
export const canRental = (user) => user && CAN_RENTAL.includes(user.role);
export const canEditRentalAssetId = (user) => user && CAN_RENTAL_EDIT_ASSET_ID.includes(user.role);
// Serial-entry permission depends on the company-level toggle fetched at runtime.
// Callers use `canSerialEntry(user, serialEntryRole)` where the second arg is
// the value returned by GET /api/company-settings.
export const canSerialEntry = (user, serialEntryRole) => {
  if (!user) return false;
  if (user.role === "SUPER_ADMIN" || user.role === "ADMIN") return true;
  // V2.0 — Any operational user may enter serials regardless of the
  // company-level serial_entry_role toggle. The toggle is retained for
  // BC / UI messaging only.
  if (OPERATIONAL_ROLES.includes(user.role)) return true;
  return false;
};
